import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _class, _class2, _descriptor;
/*
 *                     NOTICE
 *            COPYRIGHT 2025 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { BuiltInDataTypes } from "abreact/data/DataType";
import { GuidedExpressionRenderer } from "abreact/expression/ui/readonly/GuidedExpressionRenderer";
import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { RegExpMarker } from "abreact/grid/generic/RegExpMarker";
import { SanitizedString } from "abreact/grid/generic/SanitizedString";
import { CssUtils } from "abreact/incubator/ui/utils/CssUtils";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbButton } from "abreact/misc/AbButton";
import { AbMarkdownContent } from "abreact/misc/AbMarkdownContent";
import { AbSize } from "abreact/misc/AbSize";
import { AbWithTooltip } from "abreact/popup/AbWithTooltip";
import { AbBusyIndicatorSize } from "absdk/commons/ui/misc/AbBusyIndicator";
import { StringUtils } from "abweb/utils/StringUtils";
import { action, makeObservable, observable } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
/**
 * Special AbGenericGrid cell renderer for string cells when rendering in one of the fixed-height row modes (see AbGenericGridProps.fixedHeightRowMode).
 * This limits the text being rendered to one line, with a Show More button rendered if the text is clipped.
 */
export let MaxLineGridCellRenderer = observer(_class = (_class2 = class MaxLineGridCellRenderer extends React.Component {
  constructor(props) {
    super(props);
    _initializerDefineProperty(this, "isTextClipped", _descriptor, this);
    _defineProperty(this, "refSpan", void 0);
    this.refSpan = /*#__PURE__*/React.createRef();

    // If we are using this renderer, this field must be set
    this.props.column.isVariableHeightField = true;
    this.onClickShowMore = this.onClickShowMore.bind(this);
    this.onClickShowLess = this.onClickShowLess.bind(this);
    makeObservable(this);
  }
  componentDidMount() {
    this.checkSingleLineOverflow();
  }
  componentDidUpdate(prevProps) {
    this.checkSingleLineOverflow();
  }
  render() {
    return /*#__PURE__*/React.createElement("div", {
      className: ReactUtils.componentClassNamesString("max-line-grid-cell-renderer", this.props, [this.isExpanded ? "expanded" : "default-size"])
    }, this.isExpanded ? this.renderExpandedModeContent() : this.renderNormalContent());
  }

  /**
   * This method creates the tooltip to render in Normal (collapsed) mode in an AbWithTooltip wrapper for our component.
   * @param currentValueContent
   * @private
   */
  getNormalContentTooltip(currentValueContent) {
    let tooltip;
    let clippedTooltip;
    if (this.props.tooltip) {
      // We have been give a tooltip from the outside so we will render it first
      if (this.isTextClipped) {
        // If we are clipped, we want to include the currentValueContent in the tooltip since user cannot see full content
        tooltip = /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
          "data-test-id": "propsTooltip"
        }, this.props.tooltip), /*#__PURE__*/React.createElement("div", {
          "data-test-id": "currentValueLabel"
        }, AbReactResources.maxLineGridCellRenderer_currentValue()), currentValueContent);
      } else {
        // We are not clipped, so just show the tooltip given to us from the outside
        tooltip = this.props.tooltip;
      }
    } else {
      // No external tooltip
      clippedTooltip = currentValueContent;
    }
    return {
      tooltip,
      clippedTooltip
    };
  }
  renderNormalContent() {
    let content;
    let tooltip;
    let clippedTooltip;
    if (this.props.column.richDataTypeName === BuiltInDataTypes.MARKDOWN_STRING.name) {
      content = /*#__PURE__*/React.createElement("span", {
        className: "inner-content",
        ref: this.refSpan
      }, /*#__PURE__*/React.createElement(AbMarkdownContent, {
        markdown: this.props.text
      }));
      const tooltipResult = this.getNormalContentTooltip(/*#__PURE__*/React.createElement(AbMarkdownContent, {
        markdown: this.props.text
      }));
      tooltip = tooltipResult?.tooltip;
      clippedTooltip = tooltipResult?.clippedTooltip;
    } else if (this.props.column.richDataTypeName === BuiltInDataTypes.GUIDED_EXPRESSION_JSON.name) {
      content = /*#__PURE__*/React.createElement("span", {
        className: "inner-content",
        ref: this.refSpan
      }, /*#__PURE__*/React.createElement(GuidedExpressionRenderer, {
        busyIndicatorSize: AbBusyIndicatorSize.TINY,
        expression: this.props.text,
        notifyContentsResized: () => {
          this.props.onResizeCell?.();
          this.checkSingleLineOverflow();
        }
      }));
      const tooltipResult = this.getNormalContentTooltip(/*#__PURE__*/React.createElement(GuidedExpressionRenderer, {
        busyIndicatorSize: AbBusyIndicatorSize.TINY,
        expression: this.props.text
      }));
      tooltip = tooltipResult?.tooltip;
      clippedTooltip = tooltipResult?.clippedTooltip;
    } else if (this.props.column.isHtmlContent) {
      content = /*#__PURE__*/React.createElement("span", {
        className: "inner-content",
        ref: this.refSpan,
        dangerouslySetInnerHTML: {
          __html: StringUtils.sanitizeHtmlString(this.props.text)
        }
      });
      const tooltipResult = this.getNormalContentTooltip(/*#__PURE__*/React.createElement(SanitizedString, {
        text: this.props.text
      }));
      tooltip = tooltipResult?.tooltip;
      clippedTooltip = tooltipResult?.clippedTooltip;
    } else {
      content = /*#__PURE__*/React.createElement("span", {
        className: "inner-content",
        ref: this.refSpan
      }, this.props.text);
      const tooltipResult = this.getNormalContentTooltip(/*#__PURE__*/React.createElement("div", null, this.props.text));
      tooltip = tooltipResult?.tooltip;
      clippedTooltip = tooltipResult?.clippedTooltip;
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(AbWithTooltip, {
      clippedTooltip: clippedTooltip,
      tooltip: tooltip,
      wrapChildren: false
    }, content), this.renderShowMoreLink());
  }
  renderExpandedModeContent() {
    let content;
    if (this.props.findRegExp) {
      if (this.props.column.isHtmlContent) {
        const text = StringUtils.sanitizeHtmlString(this.props.text.replace(this.props.findRegExp, "<mark>$&</mark>"));
        content = /*#__PURE__*/React.createElement(SanitizedString, {
          text: text,
          className: "dsv-text-cell"
        });
      } else if (this.props.column.richDataTypeName === BuiltInDataTypes.MARKDOWN_STRING.name) {
        // Note that for AbMarkdownContent, it will pull out the findRegExp from the GridFindContext React context that the AbGenericGrid injects
        content = /*#__PURE__*/React.createElement(AbMarkdownContent, {
          markdown: this.props.text,
          isHighlighted: true
        });
      } else if (this.props.column.richDataTypeName === BuiltInDataTypes.GUIDED_EXPRESSION_JSON.name) {
        // Since guided expression rendering does not support findRegExp
        // we will probably never hit this
        content = /*#__PURE__*/React.createElement(GuidedExpressionRenderer, {
          busyIndicatorSize: AbBusyIndicatorSize.TINY,
          expression: this.props.text,
          notifyContentsResized: this.props.onResizeCell
        });
      } else {
        content = /*#__PURE__*/React.createElement(RegExpMarker, {
          text: this.props.text,
          regExp: this.props.findRegExp,
          className: "dsv-text-cell"
        });
      }
    } else {
      if (this.props.column.isHtmlContent) {
        content = /*#__PURE__*/React.createElement(SanitizedString, {
          text: this.props.text,
          className: "dsv-text-cell"
        });
      } else if (this.props.column.richDataTypeName === BuiltInDataTypes.MARKDOWN_STRING.name) {
        content = /*#__PURE__*/React.createElement(AbMarkdownContent, {
          markdown: this.props.text
        });
      } else if (this.props.column.richDataTypeName === BuiltInDataTypes.GUIDED_EXPRESSION_JSON.name) {
        content = /*#__PURE__*/React.createElement(GuidedExpressionRenderer, {
          busyIndicatorSize: AbBusyIndicatorSize.TINY,
          expression: this.props.text,
          notifyContentsResized: this.props.onResizeCell
        });
      } else {
        content = /*#__PURE__*/React.createElement("div", {
          className: "dsv-text-cell"
        }, this.props.text);
      }
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(AbWithTooltip, {
      tooltip: this.props.tooltip,
      wrapChildren: true
    }, content), this.props.isRowExpanded && /*#__PURE__*/React.createElement(AbButton, {
      label: AbReactResources.maxLineGridCellRenderer_showLess(),
      className: CssUtils.composeClassNames("show-less-button", "ab-no-border-button", this.props.isCellExpanded && "show-less-button-visible"),
      size: AbSize.EXTRA_SMALL,
      dataTestId: "btnShowLess",
      onClick: this.onClickShowLess
    }));
  }
  renderShowMoreLink() {
    if (!this.isTextClipped) {
      return null;
    }
    return /*#__PURE__*/React.createElement(AbButton, {
      label: AbReactResources.maxLineGridCellRenderer_showMore(),
      className: "overlay-button ab-no-border-button",
      size: AbSize.EXTRA_SMALL,
      dataTestId: "btnShowMore",
      onClick: this.onClickShowMore
    });
  }
  get isExpanded() {
    return this.props.isRowExpanded || this.props.findRegExp != null;
  }
  checkSingleLineOverflow() {
    if (this.isExpanded) {
      return;
    }
    if (this.refSpan.current == null) {
      return;
    }
    const isClipped = this.refSpan.current.scrollWidth > this.refSpan.current.clientWidth || this.refSpan.current.scrollHeight > this.refSpan.current.clientHeight;
    if (isClipped !== this.isTextClipped) {
      this.isTextClipped = isClipped;
    }
  }
  onClickShowMore() {
    this.props.onExpandCell(true);
  }
  onClickShowLess() {
    this.props.onExpandCell(false);
  }
}, _descriptor = _applyDecoratedDescriptor(_class2.prototype, "isTextClipped", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _applyDecoratedDescriptor(_class2.prototype, "checkSingleLineOverflow", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "checkSingleLineOverflow"), _class2.prototype), _class2)) || _class;
//# sourceMappingURL=MaxLineGridCellRenderer.js.map
